import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { BellRing, X } from "lucide-react";

const NIVELES = {
  vencido: { l: "Vencido", cls: "bg-red-600 text-white", row: "bg-red-50 text-red-700" },
  urgente: { l: "Urgente (7 días o menos)", cls: "bg-red-500 text-white", row: "bg-red-50 text-red-700" },
  atencion: { l: "Requiere atención (15 días)", cls: "bg-amber-500 text-white", row: "bg-amber-50 text-amber-800" },
  proximo: { l: "Próximo a vencer (30 días)", cls: "bg-yellow-400 text-slate-900", row: "bg-yellow-50 text-yellow-800" },
};
const ORDEN = ["vencido", "urgente", "atencion", "proximo"];
const sel = "rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm";
const fmtF = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");

function DetalleAlertas({ onClose, puedeFiltrarEmpresa }) {
  const [f, setF] = useState({ empresa: "", estado: "", desde: "", hasta: "" });
  const [data, setData] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  useEffect(() => { if (puedeFiltrarEmpresa) api.get("/empresas").then((r) => setEmpresas(r.data)).catch(() => {}); }, [puedeFiltrarEmpresa]);
  useEffect(() => { api.get("/alertas/contratos", { params: f }).then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, [f]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-slate-900/40 p-3 sm:p-6" data-testid="alertas-modal">
      <div className="w-full max-w-6xl rounded-xl bg-white p-4 shadow-xl sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <p className="font-display text-lg font-bold text-slate-900">Contratos por vencer</p>
          <button onClick={onClose} data-testid="alertas-cerrar"><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          {puedeFiltrarEmpresa && <select className={sel} value={f.empresa} onChange={set("empresa")} data-testid="alertas-filtro-empresa"><option value="">Todas las empresas</option>{empresas.map((e) => <option key={e} value={e}>{e}</option>)}</select>}
          <select className={sel} value={f.estado} onChange={set("estado")} data-testid="alertas-filtro-estado"><option value="">Todos los estados</option>{ORDEN.map((k) => <option key={k} value={k}>{NIVELES[k].l}</option>)}</select>
          <label className="flex items-center gap-1 text-xs text-slate-500">Vence desde <input type="date" className={sel} value={f.desde} onChange={set("desde")} data-testid="alertas-filtro-desde" /></label>
          <label className="flex items-center gap-1 text-xs text-slate-500">hasta <input type="date" className={sel} value={f.hasta} onChange={set("hasta")} data-testid="alertas-filtro-hasta" /></label>
        </div>
        {!data ? <p className="py-8 text-center text-sm text-slate-400">Cargando…</p> : data.items.length === 0 ? <p className="py-8 text-center text-sm text-slate-400" data-testid="alertas-vacio">No hay contratos por vencer con estos filtros.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead><tr className="border-b text-left text-xs uppercase text-slate-400">{["Empleado", "Identificación", "Empresa usuaria", "Contrato", "Cargo", "Inicio", "Terminación vigente", "Días", "Estado"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
              <tbody>{data.items.map((it) => (
                <tr key={it.vinculacion_id} className="border-b border-slate-100" data-testid={`alerta-fila-${it.vinculacion_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{it.nombre}</td><td className="px-3 py-2">{it.cedula}</td><td className="px-3 py-2">{it.empresa_cliente}</td>
                  <td className="px-3 py-2">{it.numero_contrato}</td><td className="px-3 py-2">{it.cargo}</td><td className="px-3 py-2">{it.fecha_inicio || "—"}</td>
                  <td className="px-3 py-2">{fmtF(it.fecha_terminacion)}</td><td className="px-3 py-2 font-semibold">{it.dias_restantes < 0 ? `Venció hace ${-it.dias_restantes}` : it.dias_restantes}</td>
                  <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-xs font-semibold ${NIVELES[it.nivel].row}`}>{NIVELES[it.nivel].l}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function AlertaContratos({ puedeFiltrarEmpresa }) {
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/alertas/contratos").then((r) => setRes(r.data)).catch(() => setRes(null)), []);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);
  if (!res) return null;
  const top = ORDEN.find((k) => res.counts[k] > 0);
  const cls = top ? NIVELES[top].cls : "bg-emerald-50 text-emerald-700";
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="alerta-contratos-btn" title="Contratos por vencer" className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm transition-transform hover:scale-[1.03] ${cls}`}>
        <BellRing className="h-4 w-4" /> <span className="hidden 2xl:inline">Contratos por vencer</span>
        <span className="rounded-full bg-white/90 px-2 text-slate-900" data-testid="alerta-contratos-total">{res.total}</span>
      </button>
      {open && createPortal(<DetalleAlertas puedeFiltrarEmpresa={puedeFiltrarEmpresa} onClose={() => { setOpen(false); load(); }} />, document.body)}
    </>
  );
}
